/**
 * The button, built once.
 *
 * `design/DESIGN-SYSTEM.md` §2, verbatim: "15px 600, padding 12px 24px, radius
 * 10px. Primary `--mdx-brand` on white text, hover `--mdx-brand-deep`, active
 * `translateY(1px)`. Secondary white with `--mdx-border`, hover `--mdx-ground`.
 * Ghost `--mdx-brand-tint` bg, `--mdx-brand-deep` text… Disabled `opacity: .45`,
 * `cursor: not-allowed`, no hover change."
 *
 * Every value is a token. `eslint.config.js` bans an arbitrary Tailwind value
 * and `scripts/gates.sh` bans a raw literal, so a colour or size invented here
 * fails the commit rather than the review.
 *
 * The `loading` prop replaces the LABEL and holds the button's place, because
 * that is what every auth mockup does — "Creating your account", "Logging you
 * in", "Sending the link". `auth-signup.html`'s own note: "the button holds its
 * place… No overlay, no page skeleton: one field set can only be submitted
 * once."
 *
 * Story 3.3.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'

/**
 * `DESIGN-SYSTEM.md` §2 names one destructive treatment — "Destructive
 * `--mdx-error` — irreversible actions only" — and `settings-profile.html`
 * draws TWO, which is the more careful reading and the one built here:
 *
 *   `danger-outline`  white, `--mdx-line` border, `--mdx-error` TEXT — the
 *                     trigger. Leaving an organisation is reversible (you can
 *                     be invited back), so a filled red button overstates it.
 *   `danger`          filled `--mdx-error`, white text — the CONFIRM inside the
 *                     dialog, where the action is one click from happening.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline'

const VARIANT: Record<Variant, string> = {
  primary:
    'border-transparent bg-mdx-brand text-mdx-white hover:bg-mdx-brand-deep ' +
    'disabled:hover:bg-mdx-brand active:translate-y-px',
  secondary:
    'border-mdx-line bg-mdx-white text-mdx-ink hover:bg-mdx-ground ' +
    'disabled:hover:bg-mdx-white active:translate-y-px',
  ghost:
    'border-transparent bg-mdx-brand-tint text-mdx-brand-deep hover:brightness-96 ' +
    'disabled:hover:brightness-100 active:translate-y-px',
  danger:
    'border-transparent bg-mdx-error text-mdx-white hover:brightness-96 ' +
    'disabled:hover:brightness-100 active:translate-y-px',
  'danger-outline':
    'border-mdx-line bg-mdx-white text-mdx-error hover:bg-mdx-ground ' +
    'disabled:hover:bg-mdx-white active:translate-y-px',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  /**
   * §2's "Small variant 13px, padding 8px 16px, radius 8px" — the Resend button
   * on a pending invitation row (Story 4.1), and every control that has to sit
   * inside a list row without setting its height.
   */
  size?: 'default' | 'small'
  /** Replaces the label and disables the control. Never an overlay. */
  loading?: string
  full?: boolean
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'default',
  loading,
  full = false,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || Boolean(loading)
  return (
    <button
      type="button"
      {...rest}
      disabled={isDisabled}
      aria-busy={loading ? true : undefined}
      className={[
        'inline-flex items-center justify-center gap-mdx-8',
        'font-mdx-semibold leading-none',
        size === 'small' ? '' : 'text-mdx-15',
        // `border` with NO colour here — each variant sets its own. The base
        // carried `border-transparent` at first, and since both are border-color
        // utilities of equal specificity, which one won came down to Tailwind's
        // emission order: the secondary button rendered as bare text on a white
        // card. One border-color class per button, decided by the variant.
        size === 'small'
          ? 'px-mdx-16 py-mdx-8 text-mdx-13 rounded-mdx-control-sm border'
          : 'px-mdx-24 py-mdx-12 rounded-mdx-control border',
        'transition-colors duration-mdx-swift ease-mdx-swift',
        'disabled:opacity-mdx-recessed disabled:cursor-not-allowed',
        VARIANT[variant],
        full ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? (
        <>
          <Spinner />
          {loading}
        </>
      ) : (
        children
      )}
    </button>
  )
}

/**
 * The inline spinner.
 *
 * `--mdx-dur-spin` is 700ms and exists for exactly this. `prefers-reduced-motion`
 * is honoured in `index.css` globally rather than per component, so nothing here
 * has to remember it.
 */
function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="size-mdx-14 animate-spin rounded-mdx-pill border-2 border-current border-t-transparent opacity-70"
      style={{ animationDuration: 'var(--mdx-dur-spin)' }}
    />
  )
}
